<script setup lang="ts">
import type { ArticleSize } from '~/models/Content/ContentArticle'
import type { BaseItem } from '~/models/Content/BaseItem'
import { storeToRefs } from 'pinia'
import { faExpand } from '@fortawesome/duotone-regular-svg-icons'
import useAuthStore from '~/stores/auth'
import { ImageCollectionMode } from '~/models/Content/ContentImageCollection'
import { useContentHelper } from '~/composables/useContentHelper'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import FullscreenContent from '~/components/utils/FullscreenContent.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import Button from '~/components/ui/Button.vue'
import Scene from '~/components/scene/Scene.vue'
import VideoPlayer from '~/components/media/VideoPlayer.vue'
import Image from '~/components/media/Image.vue'
import FormWriter from '~/components/formwriter/FormWriter.vue'
import FlytTask from '~/components/flyt/FlytTask.vue'
import FlashCardDeck from '~/components/flashcards/FlashCardDeck.vue'
import CodeEditor from '~/components/embeds/CodeEditor.vue'
import ConversationList from '~/components/conversation/ConversationList.vue'
import EmbedBot from '~/components/chat/EmbedBot.vue'
import ModelText from '~/components/article/ModelText.vue'
import ImageCollection from '~/components/article/ImageCollection.vue'
import ArticleTabRenderer from '~/components/article/ArticleTabRenderer.vue'
import ArticleSequence from '~/components/article/ArticleSequence.vue'
import ArticleContent from '~/components/article/ArticleContent.vue'

const {
  locationId,
  sections,
  size,
  hasSequenceSection,
} = defineProps<{
  locationId: number
  sections: BaseItem[]
  size: ArticleSize
  hasSequenceSection?: boolean
}>()

const { isAuthenticated } = storeToRefs(useAuthStore())
const {
  isArticleTabs,
  isChatbot,
  isScene,
  isModelText,
  isArticleContent,
  isImageCollection,
  isFlytTask,
  isFlashcards,
  isMedia,
  isFormWriter,
  isCodeEditor,
  isConversation,
  isVideo,
  isArticleSequence,
} = useContentHelper()
</script>

<template>
  <template
    v-for="item in sections"
    :key="item.locationId"
  >
    <ArticleContent
      v-if="isArticleContent(item)"
      :article-content="item"
      :class="{ 'flex min-h-dvh flex-col justify-center': hasSequenceSection }"
    />

    <ArticleTabRenderer
      v-else-if="isArticleTabs(item)"
      :item="item"
    >
      <template #default="{ items }">
        <ArticleContentRenderer
          :location-id
          :sections="items"
          :size
          :has-sequence-section
        />
      </template>
    </ArticleTabRenderer>

    <ModelText
      v-else-if="isModelText(item)"
      class="col-[media]"
      :content="item"
      :size="size"
    />

    <section
      v-else-if="isImageCollection(item)"
      :class="{
        '!col-[1/-1]': item.mode === ImageCollectionMode.Carousel,
        'col-[media]': Number(item.images?.destinationContentIds?.length) > 2,
        'col-[extext]': Number(item.images?.destinationContentIds?.length) <= 2,
        'flex min-h-dvh flex-col justify-center': hasSequenceSection,
      }"
    >
      <ImageCollection :content="item" />
    </section>

    <section
      v-else-if="isChatbot(item)"
      class="col-[text] mx-[calc(var(--col-gap)*-1)]"
      :class="{ 'flex min-h-dvh flex-col justify-center': hasSequenceSection }"
    >
      <EmbedBot
        :location-id="item.locationId"
        prompt=""
        class="!my-0 !p-(--col-gap)"
      />
    </section>

    <section
      v-else-if="isCodeEditor(item)"
      class="col-[text]"
      :class="{ 'flex min-h-dvh flex-col justify-center': hasSequenceSection }"
    >
      <CodeEditor
        :code="item.code"
        :libraries="item.libraries"
        :file-content-ids="item.dataFiles?.destinationContentIds"
      />
    </section>

    <section
      v-else-if="isFlytTask(item)"
      class="font-inter col-[text] mx-[calc(var(--col-gap)*-1)] rounded-xl bg-[#ffead1ff] p-(--col-gap)"
      :class="{ 'flex min-h-dvh flex-col justify-center': hasSequenceSection }"
    >
      <FlytTask
        v-if="isAuthenticated"
        :task-id="item.taskId"
        :progress-id="locationId"
        :mode="item.mode"
        :assignment="item.assignment"
        :grades="item.grades"
      />
    </section>

    <FullscreenContent
      v-else-if="isFlashcards(item)"
      class="col-[1/-1] grid bg-(--theme-50) p-(--col-width) text-(--theme-pop)"
      bg-class="bg-(--theme-50)"
    >
      <template #teleported="{ isFullscreen }">
        <FlashCardDeck
          :location-id="item.locationId"
          :is-fullscreen="isFullscreen"
          class="text-(--theme-pop)"
        />
      </template>

      <template #open="{ open }">
        <Button
          shape="square"
          class="absolute top-6 right-6"
          size="lg"
          color="white"
          @click="open"
        >
          <font-awesome-icon :icon="faExpand" />
        </Button>
      </template>
    </FullscreenContent>

    <section
      v-else-if="isVideo(item) && item.metadata"
      class="col-[text]"
      :class="{ 'flex min-h-dvh flex-col justify-center': hasSequenceSection }"
    >
      <VideoPlayer :src="item.metadata.elementURI" />
    </section>

    <FullscreenContent
      v-else-if="isMedia(item)"
      class="col-[text]"
    >
      <template #default="{ isFullscreen }">
        <Image
          :content="item"
          class="col-start-1 row-start-1"
          :class="isFullscreen ? 'p-4 place-self-center object-scale-down' : 'object-cover'"
          :use-focal-point="!isFullscreen"
        />

        <div
          v-if="item && 'caption' in item && item.caption"
          class="bg-white p-4"
        >
          <RichTextRenderer :text="item.caption" />
        </div>

        <CopyrightButton
          v-if="item && 'copyright' in item && item.copyright"
          class="col-start-1 row-start-1 place-self-end"
          :copyright="item.copyright"
          color-theme
        />
      </template>
    </FullscreenContent>

    <FullscreenContent
      v-else-if="isScene(item)"
      class="col-[media]"
    >
      <template #teleported="{ isFullscreen }">
        <Scene
          :content-id="item.contentId"
          class="bg-(--theme-5)"
          :class="{ 'h-screen p-6': isFullscreen }"
        />
      </template>

      <template #open="{ open }">
        <Button
          shape="square"
          class="absolute top-6 right-6"
          color="white"
          size="lg"
          @click="open"
        >
          <font-awesome-icon :icon="faExpand" />
        </Button>
      </template>
    </FullscreenContent>

    <section
      v-else-if="isFormWriter(item)"
      class="col-[text]"
      :class="{ 'flex min-h-dvh flex-col justify-center': hasSequenceSection }"
    >
      <FormWriter :item="item" />
    </section>

    <section
      v-else-if="isConversation(item)"
      class="col-[text]"
      :class="{ 'flex min-h-dvh flex-col justify-center': hasSequenceSection }"
    >
      <ConversationList :item="item" />
    </section>

    <ArticleSequence v-else-if="isArticleSequence(item)" :item />
  </template>
</template>
